import {
  AfterContentInit,
  AfterViewInit,
  ComponentRef,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  Injector,
  isSignal,
  OnDestroy,
  OnInit,
  QueryList,
  runInInjectionContext,
  signal,
  Signal,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { AppInjectorService } from '@app/core/services/app-injector.service';
import { DatatableExtensions } from '@app/datatables/decorators/datatable-extension.decorator';
import { ColumnSettings } from '@app/datatables/interfaces/column-settings';
import { RowReorderCallback } from '@app/datatables/interfaces/row-reorder/row-reorder-callback';
import { RowReorderDetail } from '@app/datatables/interfaces/row-reorder/row-reorder-detail';
import { RowReorderEdit } from '@app/datatables/interfaces/row-reorder/row-reorder-edit';
import { Column } from '@app/datatables/models/column';
import { CustomColumnSettings, isCustomComponent } from '@app/datatables/models/custom-column-settings';
import CustomDatatableExtension, {
  DatatableComponent,
} from '@app/datatables/models/custom-datatable-extension';
import { DatatablePaging } from '@app/datatables/models/datatable-paging';
import { OutletColumnSettings } from '@app/datatables/models/outlet-column-settings';
import { BulkActionOptions, BulkActionProcessingResults } from '@app/shared/components/bulk-actions';
import { BulkActionExecutorService } from '@app/shared/components/bulk-actions/bulk-action-executor.service';
import { CheckColumnHeaderComponent } from '@app/shared/components/check-column-header/check-column-header.component';
import { DatatableCheckColumnDefinition } from '@app/shared/components/check-column/check-column.component';
import { OutletColumn } from '@app/shared/components/datatable-outlet/datatable-outlet.component';
import { DataTableFilterDirective } from '@app/shared/directives/data-table-filter.directive';
import { DataTableDirective as PortalDataTableDirective } from '@app/shared/directives/data-table.directive';
import { TableCellDirective } from '@app/shared/directives/table-cell.directive';
import { ColumnExport } from '@app/shared/export';
import { ExportService } from '@app/shared/export/export.service';
import { SelectionDescription } from '@app/shared/models/selection-description';
import { HotkeyService } from '@app/shared/services/hotkey.service';
import { PreferenceStorageService } from '@app/shared/services/preference-storage.service';
import { debounce, Identifier, sortBy, toMap } from '@app/utils/common';
import { loggerFor } from '@app/utils/logger';
import { watch } from '@app/utils/signals/watch';
import { StringKeys } from '@app/utils/string-keys';
import { StringMap } from '@app/utils/string-map';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { AjaxData, Api, ApiPageInfo, Config } from 'datatables.net';
import { BehaviorSubject, isObservable, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { first, take } from 'rxjs/operators';
import deAt from 'src/assets/datatable/de-AT.json';
import enUs from 'src/assets/datatable/en-US.json';
import { DatatableComponentDefinition } from '../datatables/models/datatable-component-definition.interface';
import { DynamicComponentFactory } from './services/dynamic-component-factory.service';

type DataTableAjaxSubject = {
  callback: (data: any) => void;
  data: any[];
  request: AjaxData;
};

@UntilDestroy()
@Directive()
export abstract class DatatablePage<T extends object>
  implements AfterContentInit, AfterViewInit, OnDestroy, OnInit
{
  static currentInstance: DatatablePage<any> | null = null;

  @ViewChild(PortalDataTableDirective, { static: true })
  public tableElement: PortalDataTableDirective;

  @ViewChild(DataTableFilterDirective, { static: false })
  public filterComponent?: DataTableFilterDirective<T>;

  @ViewChild(DataTableFilterDirective, { static: false, read: ElementRef })
  public filterComponentRef: ElementRef;

  @ViewChildren(TableCellDirective)
  public tableCellDirectives: QueryList<TableCellDirective>;

  @ViewChildren(TableCellDirective, { read: TemplateRef })
  public templates: QueryList<TemplateRef<any>>;

  /**
   * Can be used by child pages to determine when the datatable has finished the draw.
   * Initial value is `true` because from the moment of initialization the drawing is conceptually in progress.
   */
  public drawing$: Subject<any> = new BehaviorSubject(true);

  /**
   * Indicates if at least one entry is selected
   */
  public anySelected: boolean = false;

  /**
   * List of selected entries (based on the currently displayed entries).
   */
  public currentSelectedEntries: T[] = [];

  /**
   * Signal that contains all data of the table.
   */
  public data = signal<T[]>([]);

  /**
   * Reference to the current datatable instance
   */
  public dtInstance: Api;

  /**
   * Options that will be passed to the datatable
   */
  public dtOptions: Config = {};

  /**
   * We use this trigger because fetching the list of persons can be quite long,
   * thus we ensure the data is fetched before rendering
   */
  public dtTrigger: Subject<any> = new Subject();

  /**
   * Indicates if the first row is currently selected or not.
   */
  public firstRowSelectionState: boolean = false;

  /**
   * Columns that can be toggled.
   */
  public optionalColumns: CustomColumnSettings[] = [];

  /**
   * Filled with entries that are selected by the user
   */
  public selectedEntriesMap: StringMap<Boolean> = {};

  /**
   * List of selected entries
   */
  public selectedEntries: T[] = [];

  /**
   * Value that describes how many elements are currently selected.
   */
  public selectedEntriesDescription: SelectedEntriesDescription<T> = {
    all: 'none',
    current: 'none',
  };

  /**
   * The select mode defines how the select mode will work.
   */
  public selectMode: SelectMode = SelectMode.Default;

  /**
   * If the select mode switching support is enabled.
   */
  public selectModeSwitchingEnabled: boolean = false;

  /**
   * Indicates if the table has been destroyed at least once (e.g. when toggling the select mode).
   */
  public tableWasDestroyed: boolean = false;

  /**
   * The current dataset based on the current filter and order.
   */
  protected currentData: T[] = [];

  /**
   * Can be defined in a subtype to provide custom filter functionality
   * that is applied additionally to the standard filter if it did not find a match.
   * Note: search term is propagated in lower-case.
   */
  protected customFilter: (data: T, dataIndex: number, term: string) => boolean;

  protected destroyRef: DestroyRef = inject(DestroyRef);

  /**
   * The dom property allows us to create a customized version of the datatable
   */
  protected dom: string = `
  <'datatable-row datatable-row-top'<'datatable__filter'${this.getFilter()}><'datatable__simple-pager'S>>
  <'datatable-content'tr>
  <'datatable-row datatable-row-bottom'${this.getBottomPagination()}>
  `;

  protected layout: Config['layout'] = {
    topStart: {
      className: 'datatable__filter',
      rowClass: 'datatable-row datatable-row-top',
      features: 'filter',
    },
    topEnd: {
      className: 'datatable__simple-pager',
      features: 'simplePagination',
    },
    bottom: {
      rowClass: 'datatable-row datatable-row-bottom',
      features: 'pagination',
    },
    bottomStart: null,
    bottomEnd: null,
  };

  /**
   * Logger initialised with the name of the instance, so we can set the log levels more specific
   */
  protected logger = loggerFor('DatatablePage');

  /**
   * Contains the latest datatable call to the ajax function that should reload the data from the
   * server. We use a subject, so we can combine it with the latest data$ value.
   */
  private ajax$: Subject<DataTableAjaxSubject> = new Subject();

  /**
   * Contains the current data.
   */
  private data$: Subject<T[]> = new ReplaySubject(1);

  /**
   * Emits whenever the table is destroyed.
   */
  private tableDestroyed$: Subject<void> = new Subject();

  /**
   * Columns to render
   */
  private columns: CustomColumnSettings[];

  /**
   * Columns to export (can contain columns that will not be displayed in the UI).
   */
  private columnsToExport: CustomColumnSettings[];

  /**
   * ID of the requestAnimationFrame call that is used to determine if the table has been inserted
   * into the DOM.
   */
  private checkAppearanceId: number | null = null;

  /**
   * The data that is used to create the table.
   */
  private _data: T[];

  /**
   * Components that were created by Datatables.net as extensions (e.g. filter, pagination components)
   */
  private extensionComponents: ComponentRef<any>[] = [];

  /**
   * Custom datatable extensions that are used by this instance.
   */
  private extensions = {};

  /**
   * Map of available entries
   */
  private dataMap: StringMap<T>;

  /**
   * Holds all outlet component that have been created by the attachComponents function.
   */
  private dynamicComponentMap: Map<number, Map<string, ComponentRef<any>>> = new Map();

  /**
   * Util service to create angular components
   */
  private dynamicComponentFactory: DynamicComponentFactory = inject(DynamicComponentFactory);

  /**
   * Service that exports data as csv files.
   */
  private exportService!: ExportService;

  /**
   * Subscription to the filter event of the filter component.
   */
  private filterSubscription?: Subscription;

  /**
   * Service that handles keyboard shortcuts.
   */
  private hotkeyService: HotkeyService;

  /**
   * True if the datatable has been initialized with data and the first draw finished.
   * Needed to set the data for the datatable after the first dtTrigger.next()
   */
  private initialized: boolean;

  /**
   * Necessary to support signals.
   */
  private injector = inject(Injector);

  /**
   * Tracks the life cycle phase so the datatable can react accordingly. For example the data may be
   * already available before the ngAfterViewInit callback is called (because it is cached) and
   * therefore we would want to create the table immediately.
   */
  private lifeCyclePhase: 'onInit' | 'afterContentInit' | 'afterViewInit' = 'onInit';

  /**
   * This container is used to temporarily hold any already created outlet components whilst
   * the datatable is recreating the rows.
   */
  private outletContainer?: HTMLElement;

  /**
   * Holds the row reorder callback function
   */
  private reorderedFunction?: RowReorderCallback<T, any>;

  /**
   * Defines what kind of redraw DataTables will perform
   */
  private redrawPaging?: DatatablePaging | boolean;

  /**
   * Service to persist user preferences
   * @private
   */
  private preferenceStorageService: PreferenceStorageService;

  /**
   * Internal indicator if the datatable is currently in a loading state
   */
  private _loading: boolean = true;

  public get loading() {
    return this._loading;
  }

  public set loading(value) {
    this._loading = value;
    this.drawing$.next(value);
  }

  public get selectEnabled(): boolean {
    return this.dtOptions.select !== false;
  }

  public get selectedIds(): (number | string)[] {
    const selectedEntries: (number | string)[] = [];

    for (const id in this.selectedEntriesMap) {
      if (this.selectedEntriesMap.hasOwnProperty(id)) {
        if (this.selectedEntriesMap[id] === true) {
          selectedEntries.push(id);
        }
      }
    }

    return selectedEntries;
  }

  private _selectedEntries$ = new BehaviorSubject<T[]>([]);

  public get selectedEntries$(): Observable<T[]> {
    return this._selectedEntries$.asObservable();
  }

  /**
   * Description of how many elements are currently selected.
   */
  private _selectedEntriesDescription$: Subject<SelectedEntriesDescription<T>> = new ReplaySubject(1);

  public get selectedEntriesDescription$(): Observable<SelectedEntriesDescription<T>> {
    return this._selectedEntriesDescription$.asObservable();
  }

  private get tableCellsMap() {
    const cellsWithIndex = this.tableCellDirectives
      .toArray()
      .map((item, index) => Object.assign(item, { index }));

    return toMap(cellsWithIndex, 'identifier');
  }

  public ngOnDestroy() {
    this.cancelCheckForAppearance();
    this.destroyDynamicComponents();
    this.destroyExtensionComponents();
    this.destroyTable();

    this.ajax$.complete();
    this.data$.complete();
    this.drawing$.complete();
    this.tableDestroyed$.complete();
    this._selectedEntriesDescription$.complete();
    this._selectedEntries$.complete();

    delete this.dynamicComponentMap;
    delete this.dtOptions;
  }

  public ngOnInit() {
    this.logger.debug('ngOnInit');

    this.exportService = AppInjectorService.injector.get(ExportService);
    this.hotkeyService = AppInjectorService.injector.get(HotkeyService);
    this.preferenceStorageService = AppInjectorService.injector.get(PreferenceStorageService);
    this.initialized = false;

    this.initializeDtOptions();
    this.initializeExtensions();
    this.initializeOutletColumnHandling();
    this.initializeServerSideProcessing();
    this.initializeTranslations();
    this.loadPaginationInfo();
  }

  public ngAfterContentInit(): void {
    this.logger.debug('ngAfterContentInit');

    this.lifeCyclePhase = 'afterContentInit';

    const data$ = this.fetchData();

    if (isObservable(data$)) {
      data$.subscribe((result) => {
        this.updateData(result);
      });
    } else if (isSignal(data$)) {
      runInInjectionContext(this.injector, () => {
        watch([data$], ([result]) => {
          if (result != null) {
            this.updateData(result);
          }
        });
      });
    }
  }

  public ngAfterViewInit() {
    this.logger.debug('ngAfterViewInit');

    this.lifeCyclePhase = 'afterViewInit';

    if (this._data != null) {
      this.done(this._data);
    }
  }

  /**
   * Deselect all rows
   */
  public deselectAll() {
    this.selectedEntriesMap = {};

    this.reselectEntries();
  }

  /**
   * Deselect the current page
   */
  public deselectPage() {
    const rows: any = this.dtInstance.rows();
    rows.deselect();

    this.calculateSelectedEntriesDescription();
  }

  /**
   * Destroy the current datatables.net instance.
   * @param remove If the dom elements should be removed as well.
   */
  public destroyTable(remove: boolean = false) {
    this.dtInstance?.destroy(remove);
    this.tableDestroyed$.next();

    this.dtInstance = null;
    this.tableWasDestroyed = true;
  }

  /**
   * Disable the select mode.
   */
  public disableSelectMode(): void {
    if (this.selectMode === SelectMode.Default) {
      this.toggleSelectMode({
        state: false,
      });
    } else if (this.selectMode === SelectMode.ShowOnlyForSelection) {
      this.deselectAll();
    }
  }

  /**
   * Enable the datatables.net row-reorder plugin.
   *
   * @param columnName The column with this name is used as the drag toggle and also as source of
   *                   the sort order.
   * @param updateFunction Called every time after the user changed reordered the items.
   */
  public enableReordering<OrderType>(
    columnName: string,
    updateFunction: RowReorderCallback<T, OrderType>
  ): void {
    this.dtOptions.rowReorder = {
      dataSrc: columnName,
      enable: true,
      update: false,
    };

    this.reorderedFunction = updateFunction;
  }

  /**
   * Enable the datatables.net responsive plugin. This enables the user to use the *hideOn...* and
   * *showOn...* functions of the Column class (this is used in the background when creating columns
   * with .column(...) or .outletColumn(...)).
   */
  public enableResponsive(): void {
    this.dtOptions.responsive = {
      details: false,
    };
  }

  /**
   * Enable the datatables.net select plugin. This enables the user to select rows by clicking on
   * them.
   */
  public enableSelect(): void {
    this.toggleSelectPlugin(true);
  }

  /**
   * Enable the select mode switching support.
   * @param mode Set the select mode for the table
   */
  public enableSelectModeSwitching(mode: SelectMode = SelectMode.Default): void {
    this.selectModeSwitchingEnabled = true;
    this.selectMode = mode;

    if (mode === SelectMode.ShowOnlyForSelection) {
      this.enableSelect();
    }
  }

  /**
   * Export the current dataset as a file. Currently, only csv is supported.
   *
   * @param name i18n key, which is used as name of the file.
   * @param fileNameParameters Object that is passed to the translateService when translating the fileName.
   */
  public export(name: string, fileNameParameters?: StringMap<string>): void {
    const columns = this.columnsToExport
      .filter((column) => {
        if (typeof column.export === 'object' && column.export.exportOnly) {
          return true;
        }

        return this.dtInstance.column(this.getColumnIndex(column.name)).visible() === true;
      })
      .map((column) => column.export as ColumnExport<T>);

    this.exportService.export(this.currentData, columns, name, fileNameParameters);
  }

  /**
   * Get information about the current page.
   */
  public getPageInfo(): ApiPageInfo | undefined {
    let pageInfo: ApiPageInfo;

    if (this.dtInstance != null) {
      pageInfo = this.dtInstance.page.info();
    }

    return pageInfo;
  }

  /**
   * Navigate to a specific entry.
   * @param compare Function that returns true if the correct entry has been found.
   */
  public goToEntry(compare: (entry: T) => boolean): void {
    const index = this.currentData.findIndex(compare);

    if (index > -1) {
      const newPage = Math.floor(index / this.getPageInfo().length);

      this.goToPage(newPage);
    }
  }

  /**
   * Set the current page of the table.
   * @param page Index or 'first', 'next', 'previous', 'last'
   */
  public goToPage(page: number | 'first' | 'next' | 'previous' | 'last'): void {
    if (this.dtInstance) {
      this.dtInstance.page(page).draw('page');
    }
  }

  /**
   * Hide the given column.
   *
   * @param index Index of the column starting with 0.
   */
  public hideColumn(index: number): void;

  /**
   * Hide the given column.
   *
   * @param name Name of the column (this has to match the name that was used when creating the column).
   */
  public hideColumn(name: string): void;

  public hideColumn(indexOrName: number | string): void {
    if (typeof indexOrName === 'string') {
      const index = this.getColumnIndex(indexOrName);

      if (index > -1) {
        this.setColumnVisibility(index, false);
      } else {
        this.logger.warn(`Cannot find column with name ${indexOrName}`);
      }
    } else {
      this.setColumnVisibility(indexOrName, false);
    }
  }

  /**
   * Check if the given entry has been selected by the user
   * @param entry
   */
  public isChecked(entry: T): boolean {
    const identifier = this.uniqueValue(entry);

    return !!this.selectedEntriesMap[identifier];
  }

  /**
   * Unset the initial order (by default the table is sorted by the first column).
   */
  public noInitialOrder() {
    this.dtOptions.order = [];
  }

  /**
   * Order by the defined column
   * @param columnIndex Column to sort
   * @param direction "asc" or "desc"
   */
  public orderByColumn(columnIndex: number, direction: 'asc' | 'desc') {
    this.dtOptions.order = [[columnIndex, direction]];
  }

  /**
   * Redraw the table (e.g. after the custom filter value changed)
   * @param paging This parameter is used to determine what kind of draw DataTables will perform.
   */
  public redraw(paging?: boolean | DatatablePaging) {
    this.loading = true;

    this.dtInstance.draw(paging);

    // If the datatable is in FULL_HOLD mode, we need to check if the current page still exists.
    // If not we draw the last page that is available.
    if (this.redrawPaging === DatatablePaging.FULL_HOLD) {
      const pageInfo = this.getPageInfo();

      if (pageInfo.pages <= pageInfo.page) {
        this.goToPage(pageInfo.pages - 1);
      }
    }
  }

  /**
   * Select all rows
   */
  public selectAll() {
    for (const entry of this._data) {
      const identifier = this.uniqueValue(entry);

      this.selectedEntriesMap[identifier] = true;
    }

    this.reselectEntries();
  }

  /**
   * Select all elements that are currently displayed.
   */
  public selectCurrentView(): void {
    this.selectEntries(this.currentData);
  }

  /**
   * Select entries
   * @param entries List of entries to select.
   * @param deselectOthers Deselect the currently selected entries.
   */
  public selectEntries(entries: T[], deselectOthers: boolean = true) {
    this.toggleEntries(entries, {
      deselectOthers,
      state: true,
    });

    this.reselectEntries();
  }

  /**
   * Select the current page
   */
  public selectPage() {
    this.deselectAll();

    this.togglePage({
      deselectOthers: false,
      state: true,
    });
  }

  /**
   * Set the visibility of the given column.
   *
   * @param index Index of the column starting with 0.
   * @param visible If true, the column is shown. If false, the column is hidden.
   */
  public setColumnVisibility(index: number, visible: boolean): void {
    if (this.dtInstance != null) {
      this.dtInstance.columns(index).visible(visible);
      this.columns[index].visible = visible;
      this.dtOptions.columns[index].visible = visible;

      if (visible && this.columns[index].customComponent) {
        const attach = this.attachComponents.bind(this);
        const api = this.dtInstance;

        this.dtInstance.rows().every(function (index) {
          const tr = this.node();
          const data = this.data();

          attach(api, tr, data, index);
        });
      }
    } else {
      this.columns[index].visible = visible;
      this.dtOptions.columns[index].visible = visible;
    }
  }

  /**
   * Show the given column.
   *
   * @param index Index of the column starting with 0.
   */
  public showColumn(index: number): void;

  /**
   * Show the given column.
   *
   * @param name Name of the column (this has to match the name that was used when creating the column).
   */
  public showColumn(name: string): void;

  public showColumn(indexOrName: number | string): void {
    if (typeof indexOrName === 'string') {
      const index = this.getColumnIndex(indexOrName);

      if (index > -1) {
        this.setColumnVisibility(index, true);
      } else {
        this.logger.warn(`Cannot find column with name ${indexOrName}`);
      }
    } else {
      this.setColumnVisibility(indexOrName, true);
    }
  }

  /**
   * Set the redrawPaging mode of the datatables.net plugin to FULL_HOLD. This causes the table to
   * stay on the current page after it has been redrawn.
   */
  public stayOnPageAfterRedraw(): void {
    this.redrawPaging = DatatablePaging.FULL_HOLD;
  }

  /**
   * Toggle the selection state of all entries.
   */
  public toggleAll(options?: SelectionOptions) {
    options ??= {
      state: this.selectedEntriesDescription.all !== 'all',
    };

    this.toggleEntries(this._data, options);
    this.reselectEntries();
  }

  public toggleColumn(index: number, visible?: boolean): void;
  public toggleColumn(name: string, visible?: boolean): void;
  public toggleColumn(indexOrName: number | string, visible?: boolean): void {
    if (typeof indexOrName === 'string') {
      const index = this.getColumnIndex(indexOrName);
      const visibility = visible ?? !this.isVisible(index);

      if (index > -1) {
        this.setColumnVisibility(index, visibility);
      } else {
        this.logger.warn(`Cannot find column with name ${indexOrName}`);
      }
    } else {
      const visibility = visible ?? !this.isVisible(indexOrName);
      this.setColumnVisibility(indexOrName, visibility);
    }
  }

  /**
   * Toggle the selection state of the current page.
   */
  public toggleCurrentView(options?: SelectionOptions) {
    options ??= {
      state: this.selectedEntriesDescription.current !== 'all',
    };

    this.toggleEntries(this.currentData, options);
    this.reselectEntries();
  }

  /**
   * Toggle the selection state of the current page.
   */
  public togglePage(options?: SelectionOptions) {
    const entries: T[] = Array.from(this.dtInstance.rows().data());

    this.toggleEntries(entries, options);
    this.reselectEntries();
  }

  /**
   * Toggle the select plugin of the datatable.
   */
  public toggleSelectMode(options?: SelectModeOptions): void {
    const pageInfo = this.getPageInfo();
    const order = this.dtInstance.order();

    this.deselectAll();
    this.destroyTable();
    this.toggleSelectPlugin(options?.state);

    if (this.selectEnabled && options?.selectAll) {
      this.toggleEntries(this.currentData, {
        state: true,
      });
    }

    // Table is currently sorted
    if (order.length > 0) {
      // [columnIndex, direction]
      const sort = order[0] as [number, 'asc' | 'desc'];

      if (this.selectEnabled) {
        // Move the order to one column to the right because a state column is inserted at the beginning.
        this.orderByColumn(sort[0] + 1, sort[1]);
      } else {
        // Move the order to one column to the left because the state column is removed.
        this.orderByColumn(sort[0] - 1, sort[1]);
      }
    }

    this.createTable();
    this.goToPage(pageInfo.page);
    this.reselectEntries();
    this.toggleColumnVisibilityForSelectMode(this.selectEnabled);
  }

  /**
   * Toggle the selection state of the given entry.
   * @param entry Entry to toggle.
   * @param state If true the entry is selected, if false the entry is deselected.
   */
  public toggleSelection(entry: T, state?: boolean) {
    this.toggleEntry(entry, state);
    this.reselectEntries();
  }

  /**
   * Helper function to create an outlet column
   * @param id
   * @param options
   */
  protected outletColumn<K extends StringKeys<T>>(
    id: K | string,
    options?: OutletColumnSettings<T>
  ): OutletColumn<T> {
    const tableCell = this.tableCellsMap[id];

    if (tableCell === undefined) {
      throw new Error('No table cell with id ' + id);
    }

    options = {
      className: tableCell.class,
      name: tableCell.identifier,
      orderable: tableCell.sortable,
      width: tableCell.width,
      ...options,
    };

    return new OutletColumn(tableCell.data, this.templates.toArray()[tableCell.index], options);
  }

  /**
   * Helper function to create multiple outlet columns in the given order
   */
  protected outletColumns(columns: string[]): OutletColumn<T>[] {
    return columns.map((data) => this.outletColumn(data));
  }

  protected column<K extends keyof T>(data: K, options?: ColumnSettings<T[K], T>): Column<T[K], T>;
  protected column<Data>(data: string, options?: ColumnSettings<any, T>): Column<Data, T>;
  protected column(data: string, options?: ColumnSettings<any, T>): Column<any, T> {
    return new Column<any, T>(data, options);
  }

  /**
   * Function that passes data for this page to the datatable.
   * The datatable page will ignore `undefined` values of signals.
   */
  protected abstract fetchData(): Observable<T[]> | Signal<T[] | undefined>;

  /**
   * Function that sets columns to be rendered
   */
  protected abstract getColumns(): CustomColumnSettings[];

  protected getBottomPagination(): string {
    return 'P';
  }

  protected getFilter(): string {
    return 'F';
  }

  /**
   * When the data was fetched and the component doesn't use *ngFor to create the table body
   * we do this manually by adding all rows to the datatable
   */
  protected onDataFetched(data: T[]) {
    this.data$.next(data);
  }

  protected runBulkAction(options: BulkActionOptions<T>): Promise<BulkActionProcessingResults | null> {
    return AppInjectorService.injector.get(BulkActionExecutorService).executeAction(options);
  }

  /**
   * Function that is called every time the row callback is executed by the datatable. This can be
   * used to change the row itself (e.g. to make it bold).
   *
   * @param row Table row element.
   * @param data Data object for this row.
   * @param index The index of the row.
   */
  protected rowCallback(row: HTMLTableRowElement, data: T, index: number): void {}

  /**
   * Function that returns a unique value for the given entry (uses by default the property 'id')
   * @param entry
   */
  protected uniqueValue(entry: T): Identifier {
    return (entry as any).id;
  }

  /**
   * After a draw we need to reselect all entries the user selected (the plugin itself doesn't
   * support this when using server-side processing).
   */
  private reselectEntries(): void {
    if (this.dtInstance != null) {
      const data = this.dtInstance.rows().data() as unknown as T[];
      const selectedRows = [];

      for (let i = 0; i < data.length; ++i) {
        if (this.isChecked(data[i])) {
          selectedRows.push(i);
        }
      }

      // Unselect all selected rows
      const rows: any = this.dtInstance.rows({ selected: true });
      rows.deselect();

      // Select only those that are selected
      const rowsToBeSelected: any = this.dtInstance.rows(selectedRows);

      rowsToBeSelected.select();

      this.calculateSelectedEntriesDescription();
    }
  }

  /**
   * Datatables uses jQuery to render its rows. This means that we cannot
   * simply use angular components inside the created rows. This function
   * creates dynamic components for all columns that need to be implemented
   * as an angular component
   */
  private attachComponents(dtInstance: Api, row: HTMLTableRowElement, data: T, rowIndex: number) {
    // Check if any dynamic components have already been created for this row index
    if (this.dynamicComponentMap.has(rowIndex) === false) {
      // If not create the map that is going to be filled with the created outlet components
      this.dynamicComponentMap.set(rowIndex, new Map<string, ComponentRef<any>>());
    }

    const rowComponents = this.dynamicComponentMap.get(rowIndex);
    const count: Record<string, number> = {};

    this.columns
      .filter(
        (column) => dtInstance.column(this.getColumnIndex(column.name)).visible() && isCustomComponent(column)
      )
      .map((column: CustomColumnSettings) => {
        const componentDefinition: DatatableComponentDefinition = column.customComponent;

        // Find the element that renders the custom component
        const $el = $(row).find(componentDefinition.selector);

        count[componentDefinition.selector] ??= 0;

        // If multiple outlet columns are in use we need to select the correct one for this column
        const $column = $el.eq(count[componentDefinition.selector]++);

        // Check if the current cell has already been processed
        if ($column.data('PORTAL_INITIALIZED') === undefined) {
          // Get all nodes that should be projected to the element
          const projectableNodes = [Array.prototype.slice.call($column[0].childNodes)];

          let componentRef: ComponentRef<DatatableComponent>;

          // If the map of dynamic components already contains one for this column then reuse it
          if (rowComponents.has(column.name)) {
            componentRef = rowComponents.get(column.name);

            // Replace the dom node that was created by datatables.net with angular component
            $column.replaceWith(componentRef.location.nativeElement);
          } else {
            // Create a new dynamic instance of this component and attach it to the element
            componentRef = this.dynamicComponentFactory.create<DatatableComponent>(
              componentDefinition.type,
              $column[0],
              projectableNodes
            );

            componentRef.instance.setDatatablePage(this);

            // Remember the dynamic component for this column
            rowComponents.set(column.name, componentRef);
          }

          if (column.onAfterCreate) {
            column.onAfterCreate(
              {
                ...data,
                $index: rowIndex,
              },
              componentRef.instance
            );
          }

          $column.data('PORTAL_INITIALIZED', true);
        }
      });
  }

  /**
   * Calculate how many elements are currently selected.
   */
  private calculateSelectedEntriesDescription() {
    const firstRowData = this.dtInstance?.row(0).data() as T | null;

    this.firstRowSelectionState = firstRowData ? this.isChecked(firstRowData) : false;

    this.updateSelectedEntries();
  }

  /**
   * Cancel the checkForAppearance animation frame request if it exists.
   */
  private cancelCheckForAppearance(): void {
    if (this.checkAppearanceId != null) {
      cancelAnimationFrame(this.checkAppearanceId);
      this.checkAppearanceId = null;
    }
  }

  /**
   * Clear rows and remove event handlers
   */
  private cleanup() {
    this.loading = true;

    this.destroyDynamicComponents();
    this.destroyExtensionComponents();

    if (this.dtInstance !== undefined) {
      this.logger.debug('dtInstance available - clearing');
      this.dtInstance.rows().clear();
      this.dtInstance.off('destroy deselect select');
    }
  }

  /**
   * Create the datatable instance.
   */
  private createTable(): void {
    this.updateColumns();

    if (this.filterComponent != null) {
      if (this.filterSubscription == null) {
        this.filterSubscription = this.filterComponent.filterChanged
          .pipe(untilDestroyed(this))
          .subscribe((value) => {
            // Only trigger a redraw if the filter actually changes anything.
            if (value.length !== this.currentData.length) {
              this.logger.debug('Filter change was emitted. Length is different, updating table');
              // If the length is different, it's obvious that something has changed.
              this.data$.next(value);
            } else {
              // If the length is the same, we need to check if the content has changed.
              const updatedValues = value.map((value) => this.uniqueValue(value));
              const currentValues = this.currentData.map((value) => this.uniqueValue(value));
              const anyItemDiffers = updatedValues.some((value, index) => currentValues[index] !== value);

              if (anyItemDiffers) {
                this.logger.debug(
                  'Filter change was emitted. Length is the same but the content has changed, updating table'
                );
                this.data$.next(value);
              } else {
                this.logger.info('Filter change was emitted. Nothing has changed, not updating table');
              }
            }
          });
      }

      this.extensions['F'] = this.filterComponentRef;
    }

    const table = this.getTableElement();

    this.dtInstance = $(table).DataTable(this.dtOptions);
    this.subscribeToEvents();

    if (this.selectModeSwitchingEnabled) {
      table.classList.toggle('datatable--select-mode-active', this.selectEnabled);
    }

    this.hideOptionalColumns();
  }

  /**
   * Data has been loaded, so now it's time to trigger the datatable rendering.
   */
  private done(result: T[]) {
    if (!this.initialized) {
      this.createTable();
    }

    this.onDataFetched(result);

    this.initialized = true;
    setTimeout(() => {
      this.loading = false;
    });
  }

  /**
   * Destroy all dynamic components and clear the outlet container
   */
  private destroyDynamicComponents() {
    if (this.dynamicComponentMap != null) {
      for (const [, row] of this.dynamicComponentMap) {
        for (const [, component] of row) {
          component.destroy();
        }
      }

      this.dynamicComponentMap.clear();
      this.outletContainer.remove();
    }
  }

  /**
   * Destroy extension components that were created as part of the datatable (e.g. filter, pagination).
   */
  private destroyExtensionComponents(): void {
    this.extensionComponents.forEach((component) => component.destroy());
  }

  /**
   * Filter the given data by the search `term`.
   * @param term to search for
   * @param data
   */
  private filterData(data: T[], term: string) {
    if (term !== '') {
      const columns = this.columns.filter((column) => column.data !== '');
      const searchValue = term.toLowerCase();

      data = data.filter((entry, index) => {
        let match = false;

        for (let i = 0; i < columns.length; i++) {
          const column = columns[i];

          if (column.filter !== undefined) {
            match = column.filter(entry, searchValue);

            // We don't need to check the remaining columns if we found a match
            if (match) {
              break;
            }
          } else {
            const key = column.data;
            const value = entry[key];

            if (typeof value === 'string') {
              match = value.toLowerCase().indexOf(searchValue) > -1;

              // We don't need to check the remaining columns if we found a match
              if (match) {
                break;
              }
            } else {
              // Let's see if it's a match but let's ignore the type
              // tslint:disable-next-line:triple-equals
              match = value == searchValue;

              // We don't need to check the remaining columns if we found a match
              if (match) {
                break;
              }
            }
          }
        }

        if (match === false && this.customFilter !== undefined) {
          match = this.customFilter(entry, index, searchValue);
        }

        return match;
      });
    }

    return data;
  }

  /**
   * Returns the index of the column with the given name or -1.
   *
   * @param name Name of the column (this has to match the name that was used when creating the column).
   */
  private getColumnIndex(name: string) {
    return this.columns.findIndex((column) => column.name === name);
  }

  /**
   * Get a description of how many entries out of a list are selected.
   * @param selectedEntries List of selected entries.
   * @param allEntries All entries.
   */
  private getDescription(selectedEntries: T[], allEntries: T[]): SelectionDescription {
    let description: SelectionDescription = 'none';

    if (selectedEntries.length === allEntries.length) {
      description = 'all';
    } else if (selectedEntries.length > 0) {
      description = 'some';
    }

    return description;
  }

  /**
   * Get the table dom element.
   */
  private getTableElement(): HTMLTableElement {
    const table = this.tableElement.element.nativeElement;

    if (this.tableElement != null) {
      this.tableElement.page = this;
    }

    if (table == null) {
      throw new Error('There is no table marked as portalTable or dataTable');
    }

    return table as HTMLTableElement;
  }

  private hideOptionalColumns(): void {
    for (const column of this.optionalColumns) {
      if (column.visible === false) {
        this.hideColumn(column.name);
      }
    }
  }

  /**
   * Initialize the dt options that are passed to the datatables.net plugin when it is created.
   * @private
   */
  private initializeDtOptions() {
    let selectOptions: any = false;

    this.dtOptions = {
      ajax: (request: AjaxData, callback) => {
        this.logger.debug(request, callback);

        this.data$.pipe(first()).subscribe((data) => {
          this.ajax$.next({
            callback,
            data,
            request,
          });
        });
      },
      deferRender: true,
      initComplete: (settings) => {
        /**
         * Mostly we use the loading-indicator-container to hide the table until it is
         * loaded for the first time. This causes some datatable.net plugins to not work correctly
         * because the init event is not bubbled up the dom because the table is not yet inserted
         * into the dom. To mitigate this behaviour we need to wait until the table is inserted
         * and then fire the following events manually (for now this is necessary for the responsive
         * and the row-reorder plugin).
         */
        const checkForAppearance = () => {
          const body = this.dtInstance.table(undefined).body() as HTMLElement;

          if (body.clientWidth > 0) {
            this.checkAppearanceId = null;

            $(document).trigger('preInit.dt', settings);
            $(document).trigger('init.dt', settings);

            // Adjust the column size because they are drawn while they are still invisible and this causes
            // the datatable to use `width: 0px`. With `adjust()` we adjust the size of the columns.
            this.dtInstance.columns.adjust();
          } else {
            this.checkAppearanceId = requestAnimationFrame(checkForAppearance);
          }
        };

        this.checkAppearanceId = requestAnimationFrame(checkForAppearance);
      },
      layout: this.layout,
      pagingType: 'numbers',
      pageLength: 10,
      responsive: false,
      select: selectOptions,
      serverSide: true,
      tabIndex: -1,
      drawCallback: () => {
        setTimeout(() => {
          this.loading = false;

          // Update the pageLength option, so it is correct when the table is recreated
          if (this.dtInstance != null) {
            this.dtOptions.pageLength = this.getPageInfo().length;
          }

          this.reselectEntries();
        });
      },
    };
  }

  /**
   * Set up callbacks to connect datatable extensions to this instance of the datatable page.
   */
  private initializeExtensions(): void {
    DatatableExtensions.forEach((extension: CustomDatatableExtension<any>) => {
      extension.onCreated((dtInstance: Api, element: Node, type) => {
        // Ensure that this is the correct datatable instance (this can be an issue when there are multiple tables on the same page)
        if (dtInstance.table(undefined).node() !== this.getTableElement()) {
          return;
        }

        if (this.extensions[extension.key] != null) {
          // Replace the component placeholder with the user defined component
          element.appendChild(this.extensions[extension.key].nativeElement);
        } else {
          // Create the actual component
          const component = this.dynamicComponentFactory.create<DatatableComponent>(type, element);
          component.instance.setDatatablePage(this);

          this.extensionComponents.push(component);

          dtInstance.on('destroy', function () {
            component.destroy();
          });
        }
      });
    });
  }

  /**
   * Check if the dynamicComponentFactory has been passed and if so start listening on the
   * rowCallback to attach all angular components that are used.
   */
  private initializeOutletColumnHandling() {
    if (this.dynamicComponentFactory) {
      // Assign the current DatatablePage instance because datatables.net passes the dtInstance
      // via this in the row callback.
      const self = this;

      /**
       * Remove all dynamic components that were created because the page size was larger than it is
       * now. These dynamic components are no longer necessary and slow down any paging events that
       * may occur in the future.
       *
       * If the user switches to a larger page size again then they will be recreated by the
       * attachComponents function.
       */
      const removeUnnecessaryDynamicComponents = debounce(() => {
        // "dynamicComponentMap" can be null if the user switches to another page at the exact
        // moment the loading is done (can happen because of the debounce).
        if (this.dynamicComponentMap != null) {
          const pageSize = this.dtInstance.page.info().length;

          while (this.dynamicComponentMap.size > pageSize) {
            const rowIndex = this.dynamicComponentMap.size - 1;

            const row = this.dynamicComponentMap.get(rowIndex);

            for (const [, column] of row) {
              column.destroy();
              column.location.nativeElement.remove();
            }

            this.dynamicComponentMap.delete(rowIndex);
          }
        }
      }, 0);

      const rowCallback = this.rowCallback;

      this.dtOptions.rowCallback = function (row: HTMLTableRowElement, data: T, index: number) {
        self.attachComponents(this.api(), row, data, index);

        removeUnnecessaryDynamicComponents();

        rowCallback(row, data, index);
      };

      this.outletContainer = document.createElement('div');

      this.outletContainer.className = 'datatable__outlet-container';
      this.outletContainer.style.display = 'none';

      document.body.appendChild(this.outletContainer);
    }
  }

  /**
   * Start listening to the data$ from the component and to all ajax requests that are made by the
   * datatable.
   */
  private initializeServerSideProcessing() {
    // Redraw the table whenever the data changes
    this.data$.subscribe(() => {
      if (this.initialized === true) {
        this.redraw(this.redrawPaging);
      }
    });

    this.ajax$.pipe(untilDestroyed(this)).subscribe(({ callback, data, request }) => {
      // Move the dynamic components that have been created, so we can reuse them when datatables
      // has created the new rows.
      this.moveDynamicComponentsToContainer();

      let processedData: T[];

      processedData = this.filterData(data, request.search.value);
      processedData = this.sortData(processedData, request);

      // Create the page that will be displayed
      const page = processedData.slice(request.start, request.start + request.length);

      // How many entries are available with the given filter
      const filtered = processedData.length;

      // How many entries are available total
      const total = this._data.length;

      // Save the current dataset, so we can use it for the export
      this.currentData = processedData;

      callback({
        data: page,
        draw: request.draw,
        recordsFiltered: filtered,
        recordsTotal: total,
      });
    });
  }

  /**
   * Set up the handling of language changes and the datatables translations.
   */
  private initializeTranslations(): void {
    const translateService = AppInjectorService.injector.get(TranslateService);

    this.setDatatablesDefaultOptions(translateService.currentLang);

    translateService.onLangChange.pipe(untilDestroyed(this)).subscribe((event: LangChangeEvent) => {
      this.setDatatablesDefaultOptions(event.lang);
    });
  }

  private isVisible(columnIndex: number): boolean {
    let isVisible;

    if (this.dtInstance != null) {
      isVisible = this.dtInstance.column(columnIndex).visible();
    } else {
      isVisible = this.columns[columnIndex].visible ?? true;
    }

    return isVisible;
  }

  private loadPaginationInfo(): void {
    this.preferenceStorageService
      .preference$<number>('pagination', 10)
      .pipe(take(1))
      .subscribe((pageLength) => {
        this.dtOptions.pageLength = pageLength;
      });
  }

  /**
   * Move the dynamic components that have already been created to container element.
   */
  private moveDynamicComponentsToContainer() {
    for (const [, row] of this.dynamicComponentMap) {
      for (const [, column] of row) {
        this.outletContainer.appendChild(column.location.nativeElement);
      }
    }
  }

  /**
   * Set default options for the datatables.net plugin.
   * @param locale Locale to use.
   */
  private setDatatablesDefaultOptions(locale: string): void {
    // Get the runtime jQuery instance
    const $ = window['$'];

    // Datatable default options
    $.extend($.fn.dataTable.defaults, {
      language: locale === 'de' ? deAt : enUs,
    });
  }

  /**
   * Sort the given data by the ajax request made by the datatable (only single column sort is supported)
   * @param data
   * @param ajax
   */
  private sortData(data: T[], ajax: AjaxData): T[] {
    let sortedData = data;

    if (ajax.order.length > 0) {
      const columns = this.columns;

      // We support only single column sort
      const order = ajax.order[0];

      const column = columns[order.column];
      let compareBy: string | ((T) => Identifier) = column.data;

      if (column.sort !== undefined) {
        compareBy = (entry: T) => column.sort(entry);
      } else if (typeof column.render === 'function') {
        const render = column.render;

        compareBy = (entry: T) => render.call(this.dtInstance.to$(), entry[column.data], 'sort', entry, null);
      } else if (column.data !== undefined) {
        compareBy = column.data;
      }

      sortedData = data.slice().sort(sortBy(compareBy, order.dir === 'desc', '', true));
    }

    return sortedData;
  }

  private subscribeToEvents() {
    this.dtInstance
      .off('destroy')
      .on('destroy', () => this.cleanup())
      .off('row-reorder')
      .on('row-reorder', (_, details: RowReorderDetail<T>[], edit: RowReorderEdit<T>) => {
        const rowReorder = this.dtOptions.rowReorder;

        if (this.reorderedFunction != null && details.length > 0 && typeof rowReorder === 'object') {
          const editedRow = edit.triggerRow.data();
          const editDetail = details.find((entry) => entry.oldData === editedRow[rowReorder.dataSrc]);

          this.reorderedFunction(editedRow, editDetail.newData, editDetail.oldData);
        }
      })
      .off('select')
      .on('select', (e, dtInstance: Api, type, indexes: number[]) => {
        const selectedEntries: T[] = dtInstance.rows(indexes).data() as unknown as T[];

        for (let i = 0; i < selectedEntries.length; ++i) {
          const entry = selectedEntries[i];

          this.toggleEntry(entry, true);
        }

        this.calculateSelectedEntriesDescription();
      })
      .off('deselect')
      .on('deselect', (e, dtInstance: Api, type, indexes: number[]) => {
        const deselectedEntries: T[] = dtInstance.rows(indexes).data() as unknown as T[];

        for (let i = 0; i < deselectedEntries.length; ++i) {
          const entry = deselectedEntries[i];

          this.toggleEntry(entry, false);
        }

        this.calculateSelectedEntriesDescription();
      });
  }

  /**
   * Hide or show columns that are marked as "hideInSelectMode" based on the given selectEnabled value.
   * @param selectEnabled If the select mode is enabled.
   */
  private toggleColumnVisibilityForSelectMode(selectEnabled: boolean): void {
    for (let i = 0; i < this.columns.length; i++) {
      const column = this.columns[i];

      if (column.hiddenInSelectMode && selectEnabled) {
        this.hideColumn(i);
      } else {
        this.showColumn(i);
      }
    }
  }

  /**
   * Toggle the selection state of the given entry.
   * @param entry
   * @param value
   */
  private toggleEntry(entry: T, value?: boolean) {
    const identifier = this.uniqueValue(entry);

    if (value != null) {
      this.selectedEntriesMap[identifier] = value;
    } else {
      this.selectedEntriesMap[identifier] = !this.selectedEntriesMap[identifier];
    }
  }

  /**
   * Toggle the selection state of the given entries. This does not cause the datatable to redraw
   * itself (the caller has to do this).
   * @param entries List of entries to select or deselect.
   * @param options Selection options.
   */
  private toggleEntries(entries: T[], options?: SelectionOptions): void {
    if (options?.deselectOthers) {
      // Deselect all rows
      this.deselectAll();
    }

    const state = options?.state ?? !this.firstRowSelectionState;

    // Select the given entries
    for (const entry of entries) {
      this.toggleEntry(entry, state);
    }

    this.calculateSelectedEntriesDescription();
  }

  /**
   * Toggle the select plugin.
   * @param state If true the plugin is enabled, if false it is disabled.
   */
  public toggleSelectPlugin(state: boolean = !this.selectEnabled) {
    if (state) {
      this.dtOptions.select = {
        style: 'multi+shift',
      };
    } else {
      this.dtOptions.select = false;
    }
  }

  /**
   * Get the column definitions from the child class and add column definitions for the select
   * plugin (if enabled).
   */
  private updateColumns() {
    const columns: CustomColumnSettings[] = this.getColumns();

    if (this.selectModeSwitchingEnabled) {
      if (this.selectEnabled) {
        const el = this.getTableElement();
        const headerRow = el.querySelector('thead tr:first-child');

        if (headerRow.querySelector('.portal-table__select-column') == null) {
          const th = document.createElement('th');
          th.className = 'datatable__select-column';

          headerRow.insertBefore(th, headerRow.firstChild);

          const checkHeader = this.dynamicComponentFactory.create(CheckColumnHeaderComponent, th);
          checkHeader.instance.setDatatablePage(this);
        }

        columns.unshift(
          this.column<any>('select', {
            className: 'datatable__select-column',
            name: 'select',
            orderable: false,
            width: '0px',
          })
            .withCustomComponent(DatatableCheckColumnDefinition)
            .withOnAfterCreate((row, el) => {
              el.entry = row;
            })
            .notSortable()
        );
      } else {
        const el = this.getTableElement();

        el.querySelector('.datatable__select-column')?.remove();
        el.querySelectorAll('tbody tr td:first-child').forEach((el) => el.remove());
      }
    }

    for (let column of columns) {
      if (column.options !== undefined) {
        Object.assign(column, column.options);
      }
    }

    // List of columns to be displayed in the UI (all columns that are not marked as "exportOnly").
    const viewColumns = columns.filter(
      (column) => column.export === false || column.export?.exportOnly !== true
    );

    // List of columns to export (all that are not marked with `export: false`).
    const columnsToExport = columns.filter((column) => column.export !== false);

    this.columns = viewColumns;
    this.columnsToExport = columnsToExport;
    this.dtOptions.columns = viewColumns;
    this.optionalColumns = columns.filter((column) => column.isOptional);
  }

  private updateData(result: T[]): void {
    this.data.set(result);
    this._data = result;
    this.dataMap = {};

    for (const entry of result) {
      this.dataMap[this.uniqueValue(entry)] = entry;
    }

    this.logger.debug('New data arrived', result);

    if (this.lifeCyclePhase === 'afterViewInit') {
      this.done(result);
    } else {
      this.loading = false;
    }
  }

  /**
   * Update the selected entries array
   */
  private updateSelectedEntries() {
    const selectedEntries: T[] = [];
    const currentSelectedEntries: T[] = [];
    const currentEntryMap: Record<string, T> = {};

    for (const entry of this.currentData) {
      currentEntryMap[this.uniqueValue(entry)] = entry;
    }

    for (const id of this.selectedIds) {
      const entry = this.dataMap[id];

      if (entry != null) {
        selectedEntries.push(entry);

        if (currentEntryMap[id] != null) {
          currentSelectedEntries.push(entry);
        }
      } else {
        delete this.selectedEntriesMap[id];
      }
    }

    this.anySelected = selectedEntries.length > 0;
    this.currentSelectedEntries = currentSelectedEntries;
    this.selectedEntries = selectedEntries;

    this.selectedEntriesDescription = {
      all: this.getDescription(this.selectedEntries, this._data),
      current: this.getDescription(this.currentSelectedEntries, this.currentData),
    };

    this._selectedEntries$.next(this.selectedEntries);
    this._selectedEntriesDescription$.next(this.selectedEntriesDescription);
  }
}

interface SelectedEntriesDescription<T> {
  /**
   * Description if none, some or all entries are selected.
   */
  all: SelectionDescription;

  /**
   * Description if none, some or all entries are selected based on the currently displayed entries.
   */
  current: SelectionDescription;
}

interface SelectionOptions {
  /**
   * If true currently selected entries are deselected first. Default is false.
   */
  deselectOthers?: boolean;

  /**
   * If true the elements are selected, if false they are deselected.
   */
  state?: boolean;
}

interface SelectModeOptions {
  /**
   * If true all elements that are currently visible are selected.
   */
  selectAll?: boolean;

  /**
   * If true the select mode is enabled, if false it is disabled.
   */
  state?: boolean;
}

export enum SelectMode {
  /**
   * The select mode is disabled by default and needs to be enabled manually,
   * either by pressing CTRL+A or by toggling in another way.
   */
  Default = 'Default',

  /**
   * The select mode is enabled by default, but the bulk action toolbar is
   * only visible if there is at least one entry selected.
   */
  ShowOnlyForSelection = 'ShowOnlyForSelection',
}
